import { Box, IconButton, Theme } from '@mui/material';
import { Field, FieldArray, FieldArrayRenderProps, FieldProps, FormikProps, useFormikContext } from 'formik';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import {
    FormikPoolDatePicker,
    PoolButton,
    PoolSelect,
    PoolTable,
    PoolTableColumn,
    PoolTableRow,
    PoolTextField,
    SelectOption,
} from '@synergy/components';
import { PlusCircleIcon } from '@synergy/theme';
import { PoolLineClampTypography } from '@synergy/components-next';
import { getCurrencySymbol, getSuffixCurrency } from '@synergy/utils';
import { makeStyles } from '@mui/styles';

import { ProductPriceFormValues } from '../models/form-values/product-price-form-values';
import { SpecialPriceFormValues } from '../models/form-values/special-price-form-values';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { productDetailPricesFormDataSelector } from '../store/product-detail-prices-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        marginBottom: theme.spacing(2),

        '& tr': {
            verticalAlign: 'top',
        },

        // The widths come from the column definitions: the fixed thirds this
        // table started with cannot hold five columns.
        '& th:last-child': {
            padding: 0,
        },

        '& td:last-child': {
            paddingRight: 16,
        },
    },
    addButton: {
        fontSize: 20,
    },
}));

interface ProductDetailSpecialPricesProps {
    currency: string;
    index: number;
}

export const ProductDetailSpecialPrices = (props: ProductDetailSpecialPricesProps) => {
    const { currency } = props;
    const classes = useStyles();
    const intl = useIntl();
    const locale = useAppSelector(state => bestLocaleSelector(state));
    const { supplierSpecialOffers } = useAppSelector(productDetailPricesFormDataSelector);
    const formik = useFormikContext<ProductPriceFormValues>();

    const columns: PoolTableColumn[] = [
        {
            id: 'specialPrice',
            label: <FormattedMessage id="app.product.detail.prices.specialPrice" values={{ value: '' }} />,
            sortable: false,
            width: '20%',
        },
        {
            id: 'minimumQuantity',
            label: <FormattedMessage id="app.label.quantity" />,
            sortable: false,
            width: '15%',
        },
        {
            id: 'startDate',
            label: <FormattedMessage id="app.label.startDate" />,
            sortable: false,
            width: '22%',
        },
        {
            id: 'endDate',
            label: <FormattedMessage id="app.label.endDate" />,
            sortable: false,
            width: '22%',
        },
        {
            id: 'supplierSpecialOffer',
            label: <FormattedMessage id="app.product.detail.prices.supplierSpecialOffer.header" />,
            sortable: false,
            width: '21%',
        },
        {
            id: 'action',
            sortable: false,
            width: '1%',
        },
    ];

    const prefix = `currencyPrices.${props.index}.specialPrices`;

    const supplierSpecialOfferOptions: SelectOption[] = supplierSpecialOffers.map(offer => ({
        value: offer.id,
        displayValue: (
            <Box sx={{ height: '100%', display: 'inline-flex' }}>
                <Box sx={{ mr: 4 }}>{offer.title[locale]}</Box>
                &nbsp;
                <FormattedDate value={offer.validFrom} dateStyle="short" />
                {' - '}
                <FormattedDate value={offer.validTo} dateStyle="short" />
            </Box>
        ),
    }));

    const handleSupplierSpecialOffersSelect = (
        value: number | null,
        rowIndex: number,
        form: FormikProps<ProductPriceFormValues>
    ) => {
        form.setFieldValue(`${prefix}.${rowIndex}.supplierSpecialOfferId`, value);

        const offer = supplierSpecialOffers.find(offer => offer.id === value);
        if (!offer) {
            return;
        }
        form.setFieldValue(`${prefix}.${rowIndex}.startDate`, offer.validFrom);
        form.setFieldValue(`${prefix}.${rowIndex}.endDate`, offer.validTo);
    };

    const renderSpecialOfferValue = (value?: number) => {
        const offer = supplierSpecialOffers.find(offer => offer.id === value);
        if (offer?.title[locale]) {
            return <PoolLineClampTypography>{offer?.title[locale]}</PoolLineClampTypography>;
        }
        return <FormattedMessage id="app.product.detail.prices.selectSupplierSpecialOffer" />;
    };

    const getRows = (helper: FieldArrayRenderProps) => {
        // The mask always shows a row to type into, but it must not write one:
        // this ran during render and pushed the empty row straight into Formik's
        // values — so an untouched document reported unsaved input, and the row
        // travelled into the store from there. Formik creates the entry by itself
        // as soon as someone types in it.
        const stored: SpecialPriceFormValues[] = helper.form.values.currencyPrices[props.index].specialPrices;
        const items = 0 === stored.length ? [{}] : stored;

        const handleRemove = (rowIndex: number) => {
            if (items.length === 1) {
                formik.setFieldValue(`${prefix}.${rowIndex}.price`, '');
                formik.setFieldValue(`${prefix}.${rowIndex}.minimumQuantity`, undefined);
                formik.setFieldValue(`${prefix}.${rowIndex}.startDate`, null);
                formik.setFieldValue(`${prefix}.${rowIndex}.endDate`, null);
                formik.setFieldValue(`${prefix}.${rowIndex}.supplierSpecialOfferId`, undefined); // TODO: check why the value is not changing
                return;
            }
            helper.remove(rowIndex);
        };

        const rows: PoolTableRow[] = items.map((specialPrice, rowIndex) => {
            return {
                id: specialPrice.id || rowIndex,
                specialPrice: (
                    <Field name={`${prefix}.${rowIndex}.price`}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                value={field.value === undefined ? null : field.value}
                                placeholder={intl.formatMessage(
                                    { id: 'app.product.detail.prices.specialPrice' },
                                    { value: getCurrencySymbol(currency, locale) || undefined }
                                )}
                                fullWidth={true}
                                isNumeric={true}
                                noNegativeNumbers={true}
                                suffix={getSuffixCurrency(field.value, currency, locale)}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                ),
                minimumQuantity: (
                    <Field name={`${prefix}.${rowIndex}.minimumQuantity`}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                value={field.value === undefined ? null : field.value}
                                fullWidth={true}
                                // Empty is the rule that has always applied: the
                                // price counts from the first unit.
                                placeholder={intl.formatMessage({ id: 'app.product.detail.prices.beginning' })}
                                isNumeric={true}
                                noNegativeNumbers={true}
                                startAdornment={
                                    field.value ? (
                                        <FormattedMessage id="app.product.detail.prices.starting" />
                                    ) : undefined
                                }
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                ),
                startDate: (
                    <Field name={`${prefix}.${rowIndex}.supplierSpecialOfferId`}>
                        {({ field }: FieldProps<number>) => (
                            <FormikPoolDatePicker name={`${prefix}.${rowIndex}.startDate`} disabled={!!field.value} />
                        )}
                    </Field>
                ),
                endDate: (
                    <Field name={`${prefix}.${rowIndex}.supplierSpecialOfferId`}>
                        {({ field }: FieldProps<number>) => (
                            <FormikPoolDatePicker name={`${prefix}.${rowIndex}.endDate`} disabled={!!field.value} />
                        )}
                    </Field>
                ),
                supplierSpecialOffer: (
                    <Field name={`${prefix}.${rowIndex}.supplierSpecialOfferId`}>
                        {({ field, form }: FieldProps<number>) => (
                            <PoolSelect
                                // Without a label of its own: the column says
                                // what this is, and the label pushed the field
                                // a line below the rest of the row.
                                options={supplierSpecialOfferOptions}
                                fullWidth={true}
                                onValueChange={value => handleSupplierSpecialOffersSelect(value, rowIndex, form)}
                                value={field.value ?? undefined}
                                name={field.name}
                                disabled={supplierSpecialOfferOptions.length === 0}
                                renderValue={value => renderSpecialOfferValue(value as number)}
                                placeholder={intl.formatMessage({
                                    id: 'app.product.detail.prices.selectSupplierSpecialOffer',
                                })}
                            />
                        )}
                    </Field>
                ),
                action: (
                    <IconButton onClick={() => handleRemove(rowIndex)}>
                        <PlusCircleIcon color="primary" style={{ transform: 'rotate(45deg)' }} />
                    </IconButton>
                ),
            };
        });

        return rows;
    };

    return (
        <FieldArray name={prefix}>
            {helper => (
                <div>
                    <PoolTable
                        columns={columns}
                        rows={getRows(helper)}
                        styleType="bordered"
                        className={classes.table}
                    />
                    <PoolButton
                        styleType="thirdLevel"
                        onClick={() => helper.push({})}
                        startIcon={<PlusCircleIcon color="primary" className={classes.addButton} />}
                    >
                        <FormattedMessage id="app.product.detail.prices.addSpecialPrice" />
                    </PoolButton>
                </div>
            )}
        </FieldArray>
    );
};
